import { FC, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppRoutes } from "../../common/routesUtils";
import FormPasswordField from "../../components/forms/FormPasswordField";
import FormTextField from "../../components/forms/FormTextField";
import { PublicView } from "../../components/layout/PublicView";
import { CompleteRecoverPasswordPayload, useAuthValidationSchemas } from "../../datasources/requests/auth";
import { useAuthentication } from "../../hooks/useAuthentication";

const formWidth = 500;

/**
 * ResetPassword renders the component that allows users to reset their account password.
 * @constructor
 */
const ResetPassword: FC = () => {
	const [params] = useSearchParams();
	const { t } = useTranslation("authentication");
	const navigateTo = useNavigate();
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const { CompleteRecoverPasswordSchema } = useAuthValidationSchemas();
	const { isAuthenticatingUser, authenticationError, completeRecoverPassword, signOut } = useAuthentication();

	const form = useForm<CompleteRecoverPasswordPayload>({
		defaultValues: { recoveryCode: "", password: "", passwordConfirm: "", token: params.get("token") ?? "" },
		resolver: zodResolver(CompleteRecoverPasswordSchema),
	});

	const togglePasswordVisibility = () => setShowPassword(!showPassword);

	const cancelRecoverAccount = () => {
		signOut();
		navigateTo(AppRoutes.SignIn);
	};

	const handleCompleteRecoverAccount = form.handleSubmit((payload) =>
		completeRecoverPassword(payload, cancelRecoverAccount)
	);

	return (
		<PublicView errors={[authenticationError]}>
			<Stack justifyContent="center" alignItems="center">
				<Grid container spacing={2} sx={{ width: formWidth }}>
					<Grid size={12} justifyItems="center">
						<Typography variant="h5" py={5}>
							{t("reset password")}
						</Typography>
					</Grid>
					<Grid size={12}>
						<FormTextField name="recoveryCode" fullWidth form={form} label={t("verification code")} />
					</Grid>
					<Grid size={12}>
						<FormPasswordField
							name="password"
							fullWidth
							form={form}
							label={t("new password", { ns: "feature.account" })}
							showPassword={showPassword}
							onToggleShowPassword={togglePasswordVisibility}
						/>
					</Grid>
					<Grid size={12}>
						<FormPasswordField
							name="passwordConfirm"
							fullWidth
							form={form}
							showPassword={showPassword}
							label={t("confirm password", { ns: "feature.account" })}
						/>
					</Grid>

					<Grid size={12}>
						<Stack direction="row-reverse" spacing={2}>
							<Button
								loading={isAuthenticatingUser}
								loadingPosition="start"
								className="cancel"
								onClick={cancelRecoverAccount}
							>
								{t("cancel", { ns: "common" })}
							</Button>

							<Button
								loading={isAuthenticatingUser}
								loadingPosition="start"
								className="confirm"
								onClick={handleCompleteRecoverAccount}
							>
								{t("apply", { ns: "common" })}
							</Button>
						</Stack>
					</Grid>
				</Grid>
			</Stack>
		</PublicView>
	);
};

export default ResetPassword;
